import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Gift, Users, Copy, Check, Share2, DollarSign, 
  TrendingUp, Clock, Sparkles, ArrowLeft, ExternalLink,
  Loader2, Mail, UserPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      fetchReferralData();
      fetchSettings();
    }
  }, [token]);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referral/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/referral/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Join Zazu - Get ${settings?.new_user_discount || 15}% Off!`);
    const body = encodeURIComponent(
      `Hey!\n\nI've been using Zazu for my kids' education and it's amazing! Use my referral link to get ${settings?.new_user_discount || 15}% off your first subscription:\n\n${referralData?.referral_link}\n\nYou'll love it!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Zazu Education',
          text: `Get ${settings?.new_user_discount || 15}% off Zazu with my referral link!`,
          url: referralData?.referral_link
        });
      } catch (err) {
        copyToClipboard(referralData?.referral_link);
      }
    } else {
      copyToClipboard(referralData?.referral_link);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Gift className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Referral Program</h2>
            <p className="text-slate-600 mb-6">Sign in to get your unique referral link and start earning rewards!</p>
            <Button onClick={() => navigate('/login')} className="w-full bg-emerald-600 hover:bg-emerald-700">
              Sign In to Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-emerald-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-emerald-700">Referral Program</h1>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
            Share Zazu, <span className="text-emerald-600">Earn Rewards!</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Give your friends {settings?.new_user_discount || 15}% off their first subscription, and you'll get {settings?.referrer_discount || 10}% off yours for {settings?.reward_months || 3} months!
          </p>
        </div>

        {/* How it Works */}
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
          <CardHeader>
            <CardTitle className="text-emerald-700 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xl">1</div>
                <h3 className="font-semibold text-slate-800">Share Your Link</h3>
                <p className="text-sm text-slate-600">Send your unique referral link to friends and family</p>
              </div>
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xl">2</div>
                <h3 className="font-semibold text-slate-800">They Subscribe</h3>
                <p className="text-sm text-slate-600">They get {settings?.new_user_discount || 15}% off their first subscription</p>
              </div>
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xl">3</div>
                <h3 className="font-semibold text-slate-800">You Earn Rewards</h3>
                <p className="text-sm text-slate-600">Get {settings?.referrer_discount || 10}% off for {settings?.reward_months || 3} months!</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Your Referral Link */}
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Your Referral Link
            </CardTitle>
            <CardDescription>Share this link with friends to earn rewards</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={referralData?.referral_link || ''} 
                readOnly 
                className="font-mono text-sm bg-amber-50 border-amber-200"
                data-testid="referral-link-input"
              />
              <Button 
                onClick={() => copyToClipboard(referralData?.referral_link)}
                className={copied ? "bg-emerald-600" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="copy-referral-link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <div className="flex items-center gap-2 bg-amber-100 rounded-lg p-3">
              <span className="text-amber-700 font-medium">Your Code:</span>
              <code className="bg-white px-3 py-1 rounded font-bold text-amber-800 text-lg">
                {referralData?.referral_code}
              </code>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={shareNative} className="bg-emerald-600 hover:bg-emerald-700">
                <Share2 className="w-4 h-4 mr-2" />
                Share Link
              </Button>
              <Button onClick={shareViaEmail} variant="outline" className="border-emerald-300 text-emerald-700">
                <Mail className="w-4 h-4 mr-2" />
                Share via Email
              </Button>
              <Button 
                onClick={() => copyToClipboard(referralData?.referral_link)} 
                variant="outline"
                className="border-amber-300 text-amber-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-blue-700">{referralData?.total_referrals || 0}</p>
              <p className="text-sm text-blue-600">Friends Referred</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-3xl font-bold text-emerald-700">{referralData?.active_reward_months || 0}</p>
              <p className="text-sm text-emerald-600">Reward Months</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-amber-600 mb-2" />
              <p className="text-3xl font-bold text-amber-700">{referralData?.current_discount || 0}%</p>
              <p className="text-sm text-amber-600">Current Discount</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold text-purple-700">${(referralData?.total_savings || 0).toFixed(2)}</p>
              <p className="text-sm text-purple-600">Total Saved</p>
            </CardContent>
          </Card>
        </div>

        {/* Referred Users */}
        {referralData?.referred_users?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Friends You've Referred
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referralData.referred_users.map((user, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{user.name}</p>
                        <p className="text-sm text-slate-500">
                          Joined {new Date(user.signed_up_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {user.plan || 'Subscribed'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Rewards */}
        {referralData?.rewards_earned?.filter(r => r.status === 'active')?.length > 0 && (
          <Card className="border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="text-emerald-700 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Active Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {referralData.rewards_earned.filter(r => r.status === 'active').map((reward, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                  >
                    <div>
                      <p className="font-medium text-emerald-800">
                        {reward.discount_percent}% discount
                      </p>
                      <p className="text-sm text-emerald-600">
                        From referring {reward.referred_user_name}
                      </p>
                    </div>
                    <Badge className="bg-emerald-600 text-white">
                      {reward.month}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {referralData?.total_referrals === 0 && (
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Referrals Yet</h3>
              <p className="text-slate-500 mb-4">
                Share your link with friends and family to start earning rewards!
              </p>
              <Button onClick={shareNative} className="bg-emerald-600 hover:bg-emerald-700">
                <Share2 className="w-4 h-4 mr-2" />
                Share Your Link Now
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
